<template>
    <div v-if="calculatedResult">
        <div class="row">
            <div class="col-sm-6">
                <h4 class="text-center">
                    You could save around {{ calculatedResult.savings | currency(0) }} annually adopting solar &
                    battery
                </h4>

                <img class="img-responsive" src="../../../assets/results-page/solar.png" />
            </div>

            <div class="col-sm-6">
                <div class="card _margin-top-30 _margin-bottom-40">
                    <div class="detail-card _margin-bottom-30">
                        <div class="detail-card-title">
                            Your Details
                        </div>
                        <table class="table" style="margin-bottom: -5px;">
                            <tbody>
                                <tr>
                                    <td><span class="glyphicon glyphicon-map-marker"></span> {{ address.text }}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <div class="pull-left" style="margin-top: 4px">
                                            <span class="glyphicon glyphicon-home"></span>
                                            {{ result.request.address.propertyType }}
                                        </div>

                                        <button type="button" class="btn btn-ghost btn-xs pull-right" @click="onEdit">
                                            Change
                                        </button>
                                    </td>
                                </tr>
                                <!-- <tr>
                                    <td>
                                        <span class="glyphicon glyphicon-time"></span> {{ propertyUsage.label }}
                                    </td>
                                </tr> -->
                            </tbody>
                        </table>
                    </div>

                    <h6 class="_margin-bottom-0">System cost estimate</h6>
                    <h4 class="_margin-top-0">{{ result.costModel.totalCost | currency(0) }}</h4>

                    <h6 class="_margin-bottom-0">Recommended system</h6>
                    <h4 class="_margin-top-0">
                        {{ result.request.solar.numberOfPanels }} panels with a {{
                            result.request.solar.batterySize }}kWh battery
                    </h4>

                    <h6 class="_margin-bottom-0">Recommended energy tariff</h6>
                    <div class="recommend-tariff _margin-bottom-20">
                        <div class="logo">
                            <supplier-logo :id="calculatedResult.supplierId" />
                        </div>

                        <h4>
                            {{ calculatedResult.tariffName }}
                        </h4>
                    </div>

                    <uiv-btn type="primary" @click="onArrangeCall" block>
                        Arrange a call to get started
                    </uiv-btn>
                </div>

                Figures quoted are estimated, and actual quotes may vary.<br /><br />

                The proposed cost of system is based on a typical property size for the type you selected and
                includes an estimated cost of installation associated with this size.<br /><br />

                We have calculated the potential solar generation (including optimised battery performance) and
                energy cost savings based on:

                <ol style="padding-left: 20px;" type="a">
                    <li value="a">
                        Your annual energy use, based on typical consumption for a property of your size, and broken
                        down into an average half-hourly usage pattern over the year.
                    </li>
                    <li value="b">
                        The amount of sunlight available using irradiance data specific to your location and
                        following the Microgeneration Certification Scheme (MCS) standards.
                    </li>
                    <li value="c">
                        The best published Time of Use (ToU) tariff currently available in the market at the time of
                        generating this quote.
                    </li>
                </ol>
            </div>
        </div>

        <div class="row _margin-top-20 _margin-bottom-20">
            <div class="col-xs-12">
                <uiv-btn type="ghost" block @click="toggleBreakdown">
                    Detailed Breakdown <span class="glyphicon glyphicon-chevron-right" v-if="!showSummary"></span>
                    <span class="glyphicon glyphicon-chevron-down" v-if="showSummary"></span>
                </uiv-btn>
            </div>
        </div>

        <uiv-collapse v-model="showSummary">
            <div class="row">
                <div class="col-md-12">
                    <h5>What your system will generate over the year</h5>

                    <div class="card _margin-bottom-20">
                        <system-performance-chart :chart-data="calculatedResult.electricity.monthlyBreakDowns" />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-xs-12 col-sm-12 col-md-2 _margin-bottom-20">
                    <div class="card card-stats min-height">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">You'll use</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ calculatedResult.electricity.householdConsumption | number(0) }} <small>kWh</small>
                        </h4>
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">per year</p>
                        </small>
                    </div>
                </div>

                <div class="col-xs-6 col-md-2 _margin-bottom-20">
                    <div class="card card-stats min-height">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">You'll generate</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ calculatedResult.electricity.solarGeneration | number(0) }} <small>kWh</small>
                        </h4>
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">per year</p>
                        </small>
                    </div>
                </div>

                <div class="col-xs-6 col-md-3 _margin-bottom-20">
                    <div class="card card-stats min-height">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">You'll consume</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ calculatedResult.electricity.percentageMade | number(0) }}%
                            <small>({{ calculatedResult.electricity.madeKwh |
                                number(0) }} kWh)</small>
                        </h4>
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">of what you generate</p>
                        </small>
                    </div>
                </div>
                <div class="col-xs-6 col-md-3 _margin-bottom-20">
                    <div class="card card-stats min-height">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">You'll sell</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ calculatedResult.electricity.percentageSell | number(0) }}% <small>({{
                                calculatedResult.electricity.sellKwh |
                                number(0) }} kWh)</small>
                        </h4>
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">of energy you generate to the grid</p>
                        </small>
                    </div>
                </div>

                <div class="col-xs-12 col-sm-12 col-md-2 _margin-bottom-20">
                    <div class="card card-stats min-height">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">You'll buy (Import)</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ calculatedResult.electricity.importFromGrid | number(0) }} <small>kWh</small>
                        </h4>
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">from the grid</p>
                        </small>
                    </div>
                </div>
            </div>

            <h5 class="_margin-top-30 _margin-bottom-0 hidden-xs">Your system's lifetime savings (20 years)</h5>
            <p style="max-width: none">
                Solar savings and performance estimates are calculated over a 20-year period, which aligns with the
                expected lifespan of the solar panels and typical financial return timelines. Actual results may vary
                depending on factors like energy usage, utility rates, and component maintenance or replacement.
            </p>
            <div class="row hidden-xs">
                <div class="col-xs-5 col-sm-3 _margin-bottom-20">
                    <div class="card card-stats">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">Utility bill savings</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">{{ utilityBillSaving() | currency(0) }}</h4>
                    </div>
                </div>
                <div class="col-xs-2 col-sm-1">
                    <h4 class="text-center _margin-top-30 _margin-bottom-0">-</h4>
                </div>
                <div class="col-xs-5 col-sm-3 _margin-bottom-20">
                    <div class="card card-stats">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">Net system cost</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ result.costModel.totalCost | currency(0) }}
                        </h4>
                    </div>
                </div>
                <div class="col-xs-2 col-sm-1">
                    <h4 class="text-center _margin-top-30 _margin-bottom-0">=</h4>
                </div>
                <div class="col-xs-10 col-sm-4 _margin-bottom-20">
                    <div class="card card-stats">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">Net savings</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ utilityBillSaving() - result.costModel.totalCost | currency(0) }}
                        </h4>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-6 _margin-bottom-20">
                    <div class="card">
                        <h6 class="center-block text-center">
                            Cumulative savings from going solar
                        </h6>

                        <cumulative-savings-chart :savings="utilityBillSaving()" :cost="result.costModel.totalCost"
                            :chart-data="calculatedResult.electricity.yearlyBreakDowns" />
                    </div>
                </div>

                <div class="col-sm-6 _margin-bottom-20">
                    <div class="card">
                        <h6 class="center-block text-center">
                            Annual savings from going solar
                        </h6>
                        <annual-savings-chart :cost="result.costModel.totalCost" :savings="utilityBillSaving()"
                            :chart-data="calculatedResult.electricity.yearlyBreakDowns" />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-xs-5 col-sm-6 col-md-4 _margin-bottom-20">
                    <div class="card card-stats">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">Breakeven in</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ breakEvenPoint() }}
                            <small>years</small>
                        </h4>
                    </div>
                </div>
                <div class="col-xs-7 col-sm-6 col-md-4 _margin-bottom-20">
                    <div class="card card-stats">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">Total ROI</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ ((utilityBillSaving() - result.costModel.totalCost) / result.costModel.totalCost) * 100
                                | number(0) }}%
                        </h4>
                    </div>
                </div>
                <div class="col-xs-12 col-sm-6 col-md-4 _margin-bottom-20">
                    <div class="card card-stats">
                        <small>
                            <p class="_margin-top-0 _margin-bottom-0">Annual Rate of ROI</p>
                        </small>
                        <h4 class="_margin-top-0 _margin-bottom-0">
                            {{ (utilityBillSaving() / 20) / result.costModel.totalCost * 100 |
                                number(2) }}%
                        </h4>
                    </div>
                </div>
            </div>
        </uiv-collapse>

        <uiv-modal v-model="openEditModal" title="Choose Property" @dismiss="onDismissEdit">
            <div class="row _margin-bottom-30">
                <div class="col-xs-6" v-for="p in propertyList" :key="p.name" v-if="p.id != 7 && p.id != 8">
                    <radio-button v-model="form.propertyId" :value="p.id" class="radio-icons">
                        <img v-if="p.id == 3" src="../../../assets/Terraced_House.svg" class="center-block" />
                        <img v-else-if="p.id == 4" src="../../../assets/Semi-detached_House.svg" class="center-block" />
                        <img v-else-if="p.id == 5" src="../../../assets/Bungalow.svg" class="center-block" />
                        <img v-else src="../../../assets/Detached_House.svg" class="center-block" />
                        {{ p.name }}
                    </radio-button>
                </div>
            </div>

            <!-- <h6 class="_margin-bottom-20">How much time in the day is someone at home?</h6>
            <div class="col-xs-6 col-md-3" v-for="item in houseUsageList">
                <radio-button v-model="form.propertyUsage" :value="item.id">{{ item.label }}</radio-button>
            </div> -->

            <div slot="footer">
                <uiv-btn @click="onDismissEdit">Cancel</uiv-btn>

                <uiv-btn type="primary" @click="onUpdateDetails">Update</uiv-btn>
            </div>
        </uiv-modal>

        <uiv-modal v-model="openEquireModal" size="md" title="Enquire Now" :footer="!submittedEnquire">
            <div v-if="!submittedEnquire">
                <h5>Your pathway to home decarbonisation!</h5>
                <p>
                    Share a few details, and we'll connect you with our expert installer base, helping you to a greener,
                    more carbon-efficient home.
                </p>

                <h6>Contact Information</h6>
                <div class="row _margin-bottom-20">
                    <div class="col-xs-12">
                        <label for="fullName">Full Name</label>
                        <validation-messages label="Full Name" :$v="$v.enquireForm.fullName">
                            <form-field id="fullName" type="text" v-model="enquireForm.fullName"
                                :error="$v.enquireForm.fullName.$error" />
                        </validation-messages>
                    </div>
                </div>

                <div class="row _margin-bottom-20">
                    <div class="col-xs-12">
                        <label for="email">Email</label>
                        <validation-messages label="Email" :$v="$v.enquireForm.email">
                            <form-field id="email" type="text" v-model="enquireForm.email"
                                :error="$v.enquireForm.email.$error" />
                        </validation-messages>
                    </div>
                </div>

                <div class="row _margin-bottom-20">
                    <div class="col-xs-12">
                        <label for="phone">Phone</label>
                        <validation-messages label="Phone" :$v="$v.enquireForm.phone">
                            <form-field id="phone" type="text" v-model="enquireForm.phone"
                                :error="$v.enquireForm.phone.$error" maxlength="11" minlength="10"
                                @blur="$v.enquireForm.phone.$touch()" />
                        </validation-messages>
                    </div>
                </div>

                <div class="row _margin-bottom-20">
                    <div class="col-xs-12">
                        <label for="contactTime">When is the best time to contact you?</label>
                        <validation-messages label="Best time to contact" :$v="$v.enquireForm.contactTime">
                            <select-list id="contactTime" v-model="enquireForm.contactTime"
                                :list="[{ label: 'AM', value: 'AM' }, { label: 'PM - Afternoon', value: 'PM - Afternoon' }, { label: 'PM - Evening', value: 'PM - Evening' }]"
                                :error="$v.enquireForm.contactTime.$error" />
                        </validation-messages>
                    </div>
                </div>

                <div class="row _margin-bottom-20" v-if="quoteSession.solar || quoteSession.ashp">
                    <div class="col-xs-12">
                        <label>Your Property</label>
                        <div>{{ result.request.address.propertyType }}</div>
                    </div>
                </div>

                <label>Are you considering adopting asset(s) this year?</label>
                <div class="row _margin-bottom-20">
                    <div class="col-xs-4">
                        <radio-button v-model="enquireForm.adoptAsset" value="'yes'"
                            :error="$v.enquireForm.adoptAsset.$error">Yes</radio-button>
                    </div>
                    <div class="col-xs-4">
                        <radio-button v-model="enquireForm.adoptAsset" value="'no'"
                            :error="$v.enquireForm.adoptAsset.$error">No</radio-button>
                    </div>
                    <div class="col-xs-4">
                        <radio-button v-model="enquireForm.adoptAsset" value="'unsure'"
                            :error="$v.enquireForm.adoptAsset.$error">Unsure</radio-button>
                    </div>
                </div>

                <div class="row _margin-bottom-20">
                    <div class="col-xs-12">
                        <label for="info">Please provide any additional information</label>
                        <textarea id="info" class="form-control" v-model="enquireForm.additionalInfo"></textarea>
                    </div>
                </div>

                <div class="row  _margin-bottom-10">
                    <div class="col-xs-12">
                        <small>
                            <b>Privacy Notice</b><br />
                            Information collected through this form will be used for the sole purpose for processing
                            your enquiry, connecting you with our services.
                            We will share the information you provide with our trusted partners or affiliates who assist
                            us in fulfilling your request.
                        </small>
                    </div>
                </div>
            </div>

            <div class="text-center" v-else>
                <div class="tick-container">
                    <div class="tick"></div>
                </div>

                <h4 class="_margin-bottom-0">Thank you!</h4>
                <p class="_margin-top-0">
                    Your enquiry has been submitted
                </p>
            </div>

            <div slot="footer">
                <div class="row">
                    <div class="col-xs-6">
                        <uiv-btn size="sm" block @click="onDismissedEquireNow">
                            Close
                        </uiv-btn>
                    </div>
                    <div class="col-xs-6">
                        <uiv-btn type="primary" size="sm" block @click="onEnquire">
                            Enquire
                        </uiv-btn>
                    </div>
                </div>
            </div>
        </uiv-modal>
    </div>
    <div v-else>
        <div class="alert alert-info _margin-top-30">
            No result found
        </div>
    </div>
</template>

<script>
import SupplierLogo from '../../../../base/components/supplier-logo/SupplierLogo';
import RadioButton from '../../../../base/components/forms/RadioButton';
import CheckboxButton from '../../../../base/components/forms/CheckboxButton';
import { mapState } from 'vuex';
import { required, helpers, numeric, maxLength, minLength } from 'vuelidate/lib/validators';

import SystemPerformanceChart from './SystemPerformanceChart';
import CumulativeSavingsChart from './CumulativeSavingsChart';
import AnnualSavingsChart from './AnnualSavingsChart';

export default {
    name: 'SolarBatteryResults',
    data() {
        return {
            submittedEnquire: false,
            openEditModal: false,
            openEquireModal: false,
            showSummary: false,
            form: {
                propertyId: null,
                propertyUsage: null
            },
            enquireForm: {
                asset: 'SolarBattery',
                fullName: null,
                email: null,
                phone: null,
                contactTime: null,
                additionalInfo: null,
                adoptAsset: null
            }
        };
    },
    validations: {
        form: {
            propertyId: { required },
            propertyUsage: { required }
        },
        enquireForm: {
            fullName: { required },
            email: {
                required,
                regex: helpers.regex('email', /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/)
            },
            phone: {
                required,
                numeric,
                maxLength: maxLength(11),
                minLength: minLength(10),
                regex: helpers.regex('phone', /^(?=.*\d)(?=.*[1-9]).{1,11}$/)
            },
            contactTime: { required },
            adoptAsset: { required }
        }
    },
    computed: {
        ...mapState({
            quote: ({ quote }) => quote,
            address: ({ session }) => session.quote.address,
            quoteSession: ({ session }) => session.quote_journey_session,
            result: ({ quote_results }) => quote_results.result,
            propertyList: ({ lookups }) => lookups.esaPropertyTypes,
            houseUsageList: ({ lookups }) => lookups.houseUsageTypes,
            calculatedResult: ({ quote_results }) => quote_results.result.calculatedResults.find(({ isCurrent }) => {
                return isCurrent == false;
            })
        })
    },
    methods: {
        onEdit() {
            this.form.propertyId = this.result.request.address.propertyTypeId;
            this.openEditModal = true;

            this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Change' });
            this.$store.dispatch('tracing/saveActions');
        },
        async onUpdateDetails() {
            await this.$store.dispatch('quote/editQuote', {
                propertyTypeId: this.form.propertyId
            });
            await this.$store.dispatch('quote_results/getQuoteResults');
            this.openEditModal = false;

            const property = this.propertyList.find(({ id }) => this.form.propertyId == id);

            this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Updated' });
            this.$store.dispatch('tracing/addAction', { name: 'Value', value: property.name });
            this.$store.dispatch('tracing/saveActions');
        },
        onArrangeCall() {
            this.openEquireModal = true;
            this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Arrange a call to get started' });
            this.$store.dispatch('tracing/saveActions');
        },
        async onEnquire() {
            this.$v.enquireForm.$touch();
            if (this.$v.enquireForm.$invalid) return;
            const data = {
                ...this.enquireForm,
                address: this.address.text,
                houseType: this.result.request.address.propertyType,
                annualEstimateSavings: this.calculatedResult.savings,
                systemCost: this.result.costModel.totalCost,
                recommendedSystem: `${this.result.request.solar.numberOfPanels} panels with a ${this.result.request.solar.batterySize}kWh battery`
            };

            await this.$store.dispatch('quote_results/enquire', data);
            this.submittedEnquire = true;

            this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Enquire Now Submitted' });
            this.$store.dispatch('tracing/saveActions');
        },
        onDismissEdit() {
            this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Edit Property Cancelled' });
            this.$store.dispatch('tracing/saveActions');
            this.openEditModal = false;
        },
        onDismissedEquireNow() {
            this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Enquire Now Cancelled' });
            this.$store.dispatch('tracing/saveActions');
            this.openEquireModal = false;
        },
        toggleBreakdown() {
            this.showSummary = !this.showSummary;
            this.$store.dispatch('tracing/addAction', { name: 'Action', value: `${this.showSummary ? 'Show' : 'Hide'} Breakdown` });
            this.$store.dispatch('tracing/saveActions');
        },
        utilityBillSaving() {
            let total = 0;
            if (!this.calculatedResult) {
                return total;
            }

            const values = this.calculatedResult.electricity.yearlyBreakDowns;

            for (let index = 0; index < values.length; index++) {
                const element = values[index];
                total += element.savings;
            }

            return total;
        },
        breakEvenPoint() {
            const years = '-';
            if (!this.calculatedResult) {
                return years;
            }
            let totalCost = -1 * (this.result.costModel.totalCost);
            const yearlyBreakDowns = this.calculatedResult.electricity.yearlyBreakDowns;

            for (let index = 0; index < yearlyBreakDowns.length; index++) {
                const element = yearlyBreakDowns[index];
                const savings = element.savings;

                totalCost += savings;

                if (totalCost > 0) {
                    return element.year;
                }
            }

            return years;
        },
        convertToPositive(number) {
            return number < 0 ? -1 * number : number;
        }
    },
    components: {
        SupplierLogo,
        RadioButton,
        CheckboxButton,
        SystemPerformanceChart,
        CumulativeSavingsChart,
        AnnualSavingsChart
    }
};

</script>

<style lang="scss" scoped>
.detail-card {
    padding: 0 15px 15px 15px;
    border-radius: 10px;
    border: 1px solid #dadada;

    .detail-card-title {
        font-size: 16px;
        padding: 13px 15px;
        margin: 0 -15px 10px -15px;
        border-bottom: 1px solid #dadada;
        font-weight: bold;

        .btn {
            position: relative;
            top: -3px;
        }
    }
}

h4 {
    max-width: none;

    @media (max-width: 767px) {
        font-size: 20px;
    }
}

.img-responsive {
    @media (max-width:767px) {
        max-width: 70%;
        margin: auto;
    }
}

.card-stats {
    p {
        font-size: 14px;
        line-height: 1.5;
    }

    small {
        font-size: 65%;
    }

    &.min-height {
        @media (min-width: 992px) {
            min-height: 115px;
        }
    }

    @media (max-width: 767px) {
        p {
            font-size: 13px;
        }
    }
}

.recommend-tariff {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: row;
    align-items: center;

    h4 {
        padding-left: 15px;
        margin: 0;
    }
}

.tick-container {
    display: inline-block;
    position: relative;
    width: 100px;
    height: 100px;
    background-color: darken($brand-colour-primary, 10%);
    border-radius: 50%;

    .tick {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        margin: auto;
        width: 32px;
        height: 24px;
        background: url('../../../assets/tick.png') no-repeat;
        background-size: contain;
        background-position: 100% 0;
    }
}
</style>
